import { useEffect, useState } from 'react'
import { data as remixData, useNavigate, useSearchParams } from 'react-router'
import { QuoteFlowLayout } from '#app/components/cotizacion/QuoteFlowLayout.tsx'
import { AutoEmailConfirmationScreen } from '#app/components/cotizacion/screens/AutoEmailConfirmationScreen.tsx'
import { CarDetailsScreen } from '#app/components/cotizacion/screens/CarDetailsScreen.tsx'
import { PersonalInfoScreen } from '#app/components/cotizacion/screens/PersonalInfoScreen.tsx'
import { PreAutoStepScreen } from '#app/components/cotizacion/screens/PreAutoStepScreen.tsx'
import { StepSelectionScreen } from '#app/components/cotizacion/screens/StepSelectionScreen.tsx'
import { Modal } from '#app/components/ui/modal.tsx'
import { ProgressBar } from '#app/components/ui/progress-bar.tsx'
import { sendCotizacionAutoEmail } from '#app/utils/email.server.ts'
import { type Route } from './+types/cotizasimple-auto'

export const meta: Route.MetaFunction = () => [
	{ title: '100prevalor - Cotización Simple Seguro de Auto' },
	{
		name: 'description',
		content:
			'Cotiza tu seguro de auto en simples pasos. Recibe tu cotización por email.',
	},
]

type AutoFormData = {
	carBrand: string
	carModel: string
	carYear: string
	carVersion: string
	carUsage: string
	carLocation: string
	licensePlate: string
	fullName: string
	email: string
	phone: string
	dni: string
	selectedPlan: string
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const quoteDataString = formData.get('quoteData')

	if (typeof quoteDataString !== 'string') {
		return remixData(
			{ success: false, error: 'Datos inválidos' },
			{ status: 400 },
		)
	}

	const quoteData = JSON.parse(quoteDataString) as AutoFormData

	try {
		await sendCotizacionAutoEmail(quoteData)
		return remixData({ success: true })
	} catch (error) {
		console.error('Error sending email:', error)
		return remixData(
			{ success: false, error: 'Error al enviar el email' },
			{ status: 500 },
		)
	}
}

export default function CotizaSimpleAutoPage() {
	const [searchParams, setSearchParams] = useSearchParams()
	const navigate = useNavigate()
	const step = parseInt(searchParams.get('step') || '1', 10)

	// Track completed steps - step 1 is always accessible
	const [completedSteps, setCompletedSteps] = useState<Set<number>>(
		new Set([1]),
	)

	const [quoteData, setQuoteData] = useState({
		carBrand: '',
		carModel: '',
		carYear: '',
		carVersion: '',
		carUsage: '',
		carLocation: '',
		licensePlate: '',
		fullName: '',
		email: '',
		phone: '',
		dni: '',
		selectedPlan: 'ceibo', // Plan por defecto
	})

	// Total steps: 1. Selection, 2. PreAuto, 3. CarDetails, 4. PersonalInfo, 5. Confirmation
	const totalSteps = 5

	// Calculate progress based on step
	const getProgressForStep = (currentStep: number): number => {
		const progressMap: Record<number, number> = {
			1: 0, // StepSelectionScreen - no progress bar
			2: 0, // PreAutoStepScreen - no progress bar
			3: 15, // CarDetailsScreen
			4: 60, // PersonalInfoScreen
			5: 100, // AutoEmailConfirmationScreen
		}
		return progressMap[currentStep] || 0
	}

	const currentProgress = getProgressForStep(step)
	const showProgressBar = step >= 3 // Only show from CarDetailsScreen onwards

	// Validate step access
	useEffect(() => {
		if (!completedSteps.has(step)) {
			// Redirect to last completed step
			const lastCompleted = Math.max(...Array.from(completedSteps))
			setSearchParams({ step: String(lastCompleted) })
		}
	}, [step, completedSteps, setSearchParams])

	const handleNext = async (data?: Record<string, any>) => {
		if (data) {
			setQuoteData((prev) => ({ ...prev, ...data }))
		}

		// Check if we need to skip to a specific step (for direct selection)
		const targetStep = data?.skipToStep

		if (targetStep && targetStep <= totalSteps) {
			// Mark all steps up to target as accessible
			const stepsToComplete = Array.from(
				{ length: targetStep },
				(_, i) => i + 1,
			)
			setCompletedSteps((prev) => new Set([...prev, ...stepsToComplete]))
			setSearchParams({ step: String(targetStep) })
		} else if (step < totalSteps) {
			// Special handling when moving to confirmation screen (step 5)
			if (step === 4) {
				// Moving from PersonalInfoScreen to confirmation
				// Send email here
				const mergedData = { ...quoteData, ...data }

				// Extract only the fields needed for the email schema
				const emailData: AutoFormData = {
					carBrand: mergedData.carBrand || '',
					carModel: mergedData.carModel || '',
					carYear: mergedData.carYear || '',
					carVersion: mergedData.carVersion || '',
					carUsage: mergedData.carUsage || '',
					carLocation: mergedData.carLocation || '',
					licensePlate: mergedData.licensePlate || '',
					fullName: mergedData.fullName || '',
					email: mergedData.email || '',
					phone: mergedData.phone || '',
					dni: mergedData.dni || '',
					selectedPlan: mergedData.selectedPlan || 'ceibo',
				}

				console.log('📧 Intentando enviar email desde paso 4...')
				console.log('📋 Datos limpios para email:', emailData)

				// Update state with clean data for confirmation screen
				setQuoteData(emailData)

				try {
					const formData = new FormData()
					formData.append('quoteData', JSON.stringify(emailData))

					const response = await fetch('/cotizasimple-auto', {
						method: 'POST',
						body: formData,
					})

					const result = await response.json()
					console.log('📬 Respuesta del servidor:', result)

					if (response.ok) {
						console.log('✅ Email enviado exitosamente')
					} else {
						console.error('❌ Error al enviar email:', result)
					}
				} catch (error) {
					console.error('❌ Error al enviar email:', error)
				}
			}

			// Normal flow: go to next step
			setCompletedSteps((prev) => new Set([...prev, step + 1]))
			setSearchParams({ step: String(step + 1) })
		}
	}

	const handleBack = () => {
		if (step > 1) {
			setSearchParams({ step: String(step - 1) })
		}
	}

	const handleClose = () => {
		void navigate('/seguros')
	}

	const handleRestart = () => {
		setQuoteData({
			carBrand: '',
			carModel: '',
			carYear: '',
			carVersion: '',
			carUsage: '',
			carLocation: '',
			licensePlate: '',
			fullName: '',
			email: '',
			phone: '',
			dni: '',
			selectedPlan: 'ceibo', // Plan por defecto
		})
		setCompletedSteps(new Set([1]))
		setSearchParams({ step: '1' })
	}

	const renderStep = () => {
		switch (step) {
			case 1:
				return <StepSelectionScreen onNext={handleNext} />
			case 2:
				return (
					<PreAutoStepScreen
						onNext={handleNext}
						onBack={handleBack}
						data={quoteData}
					/>
				)
			case 3:
				return (
					<CarDetailsScreen
						onNext={handleNext}
						onBack={handleBack}
						data={quoteData}
					/>
				)
			case 4:
				return (
					<PersonalInfoScreen
						onNext={handleNext}
						onBack={handleBack}
						data={quoteData}
					/>
				)
			case 5:
				return (
					<AutoEmailConfirmationScreen
						onBack={handleRestart}
						data={quoteData}
					/>
				)
			default:
				return <StepSelectionScreen onNext={handleNext} />
		}
	}

	return (
		<>
			{/* Mobile: full page */}
			<div className="md:hidden">
				<QuoteFlowLayout
					currentStep={step}
					totalSteps={totalSteps}
					onBack={step > 1 ? handleBack : undefined}
					onClose={step === 1 ? handleClose : undefined}
					progressBar={
						showProgressBar ? (
							<ProgressBar progress={currentProgress} icon="car-04" />
						) : undefined
					}
				>
					{renderStep()}
				</QuoteFlowLayout>
			</div>

			{/* Desktop: modal */}
			<div className="hidden md:block">
				<Modal
					isOpen={true}
					onClose={handleClose}
					showCloseButton={false}
					rounded={false}
					maxWidth="xl"
				>
					<QuoteFlowLayout
						currentStep={step}
						totalSteps={totalSteps}
						onBack={step > 1 ? handleBack : undefined}
						onClose={step === 1 ? handleClose : undefined}
						progressBar={
							showProgressBar ? (
								<ProgressBar progress={currentProgress} icon="car-04" />
							) : undefined
						}
					>
						{renderStep()}
					</QuoteFlowLayout>
				</Modal>
			</div>
		</>
	)
}
